import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Typography } from 'antd';

const { Title } = Typography;

const COLORS = ['#4F6BF6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#F97316'];

interface ChartData {
  name?: string;
  value?: number;
  [key: string]: any;
}

// ─── Custom Tooltip ──────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: '#1F2937',
        borderRadius: 8,
        padding: '10px 14px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        border: 'none',
      }}
    >
      <p style={{ color: '#9CA3AF', fontSize: 11, margin: '0 0 4px', fontWeight: 500 }}>{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} style={{ color: '#fff', fontSize: 13, margin: 0, fontWeight: 600 }}>
          {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
        </p>
      ))}
    </div>
  );
};

// ─── Line Chart ──────────────────────────────────────────────────

interface LineChartProps {
  data: ChartData[];
  dataKey: string;
  xKey?: string;
  title?: string;
  height?: number;
  color?: string;
}

export const SimpleLineChart: React.FC<LineChartProps> = ({
  data,
  dataKey,
  xKey = 'name',
  title,
  height = 300,
  color = '#4F6BF6',
}) => (
  <div style={{ width: '100%' }}>
    {title && (
      <Title level={5} style={{ marginBottom: 16, fontWeight: 600, color: '#1F2937' }}>
        {title}
      </Title>
    )}
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
        <XAxis
          dataKey={xKey}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 11, fill: '#6B7280' }}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 11, fill: '#9CA3AF' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2.5}
          dot={{ r: 4, fill: '#fff', stroke: color, strokeWidth: 2 }}
          activeDot={{ r: 6, fill: color, stroke: '#fff', strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

// ─── Bar Chart ───────────────────────────────────────────────────

interface BarChartProps {
  data: ChartData[];
  dataKey: string;
  xKey?: string;
  title?: string;
  height?: number;
  color?: string;
}

const BAR_COLORS = ['#4F6BF6', '#6C7FF7', '#8393F9', '#99A7FA', '#B0BBFB', '#C7CEFC', '#D4DAFD', '#E1E5FE', '#EBEEFE', '#F3F5FF'];

const HorizontalBarLabel = ({ x, y, width, height: h, value }: any) => {
  if (width < 30) {
    return (
      <text
        x={x + width + 8}
        y={y + h / 2}
        fill="#4B5563"
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={600}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </text>
    );
  }
  return (
    <text
      x={x + width - 10}
      y={y + h / 2}
      fill="#fff"
      textAnchor="end"
      dominantBaseline="middle"
      fontSize={12}
      fontWeight={600}
    >
      {typeof value === 'number' ? value.toLocaleString() : value}
    </text>
  );
};

const HorizontalBarTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: '#1F2937',
        borderRadius: 10,
        padding: '12px 16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <p style={{ color: '#D1D5DB', fontSize: 11, margin: '0 0 6px', fontWeight: 500, letterSpacing: 0.3 }}>
        {label}
      </p>
      <p style={{ color: '#fff', fontSize: 15, margin: 0, fontWeight: 700 }}>
        {typeof payload[0].value === 'number' ? payload[0].value.toLocaleString() : payload[0].value}
        <span style={{ color: '#9CA3AF', fontSize: 11, fontWeight: 400, marginLeft: 4 }}>requests</span>
      </p>
    </div>
  );
};

export const SimpleBarChart: React.FC<BarChartProps> = ({
  data,
  dataKey,
  xKey = 'name',
  title,
  height = 300,
}) => {
  const dynamicHeight = Math.max(height, data.length * 48 + 40);

  return (
    <div style={{ width: '100%' }}>
      {title && (
        <Title level={5} style={{ marginBottom: 20, fontWeight: 600, color: '#111827', fontSize: 16 }}>
          {title}
        </Title>
      )}
      <ResponsiveContainer width="100%" height={dynamicHeight}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 60, left: 8, bottom: 4 }}
          barCategoryGap="24%"
        >
          <defs>
            {data.map((_, i) => (
              <linearGradient key={i} id={`hbarGrad-${i}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={BAR_COLORS[i % BAR_COLORS.length]} stopOpacity={0.9} />
                <stop offset="100%" stopColor={BAR_COLORS[i % BAR_COLORS.length]} stopOpacity={1} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
          />
          <YAxis
            type="category"
            dataKey={xKey}
            axisLine={false}
            tickLine={false}
            width={200}
            tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }}
          />
          <Tooltip content={<HorizontalBarTooltip />} cursor={{ fill: 'rgba(79, 107, 246, 0.04)' }} />
          <Bar
            dataKey={dataKey}
            radius={[0, 8, 8, 0]}
            label={<HorizontalBarLabel />}
            animationDuration={800}
            animationEasing="ease-out"
          >
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={`url(#hbarGrad-${index})`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── Curved Area Chart (Tenant Comparison) ──────────────────────

interface TenantAreaChartProps {
  data: ChartData[];
  dataKey: string;
  xKey?: string;
  title?: string;
  height?: number;
}

const TenantAreaTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: '#1F2937',
        borderRadius: 10,
        padding: '12px 16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <p style={{ color: '#D1D5DB', fontSize: 11, margin: '0 0 8px', fontWeight: 500, letterSpacing: 0.3 }}>
        {label}
      </p>
      {payload.map((entry: any, i: number) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < payload.length - 1 ? 4 : 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color }} />
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            <span style={{ color: '#9CA3AF', fontSize: 11, fontWeight: 400, marginLeft: 4 }}>requests</span>
          </span>
        </div>
      ))}
    </div>
  );
};

export const SimpleVerticalBarChart: React.FC<TenantAreaChartProps> = ({
  data,
  dataKey,
  xKey = 'name',
  title,
  height = 300,
}) => {
  // Transform flat data [{name, requests}] into a series format for multi-area chart
  // Each tenant becomes its own Area with a single data point spread across a curve
  const tenantKeys = data.map((item) => (item as any)[xKey] || (item as any).name);
  const chartData = [
    { point: '', ...Object.fromEntries(tenantKeys.map((k) => [k, 0])) },
    { point: 'Usage', ...Object.fromEntries(data.map((item) => [(item as any)[xKey] || (item as any).name, (item as any)[dataKey]])) },
    { point: '', ...Object.fromEntries(tenantKeys.map((k) => [k, 0])) },
  ];

  return (
    <div style={{ width: '100%' }}>
      {title && (
        <Title level={5} style={{ marginBottom: 20, fontWeight: 600, color: '#111827', fontSize: 16 }}>
          {title}
        </Title>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
          <defs>
            {tenantKeys.map((_, i) => (
              <linearGradient key={`tenantGrad-${i}`} id={`tenantGrad-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.4} />
                <stop offset="100%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.05} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="point"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#6B7280' }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
          />
          <Tooltip content={<TenantAreaTooltip />} />
          {tenantKeys.map((key, i) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stroke={COLORS[i % COLORS.length]}
              strokeWidth={2.5}
              fill={`url(#tenantGrad-${i})`}
              dot={{ r: 5, fill: '#fff', stroke: COLORS[i % COLORS.length], strokeWidth: 2.5 }}
              activeDot={{ r: 7, fill: COLORS[i % COLORS.length], stroke: '#fff', strokeWidth: 2.5 }}
            />
          ))}
          <Legend
            verticalAlign="bottom"
            iconType="circle"
            iconSize={8}
            formatter={(value: string) => (
              <span style={{ color: '#4B5563', fontSize: 12 }}>{value}</span>
            )}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── Pie Chart ───────────────────────────────────────────────────

interface PieChartProps {
  data: ChartData[];
  dataKey: string;
  nameKey?: string;
  title?: string;
  height?: number;
  onSliceClick?: (name: string) => void;
}

const PieTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div
      style={{
        background: '#1F2937',
        borderRadius: 8,
        padding: '10px 14px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        border: 'none',
      }}
    >
      <p style={{ color: '#9CA3AF', fontSize: 11, margin: '0 0 4px', fontWeight: 500 }}>{d.name}</p>
      <p style={{ color: '#fff', fontSize: 13, margin: 0, fontWeight: 600 }}>
        {typeof d.value === 'number' ? d.value.toLocaleString() : d.value} ({d.payload?.percent != null ? `${(d.payload.percent * 100).toFixed(0)}%` : ''})
      </p>
    </div>
  );
};

const RADIAN = Math.PI / 180;
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: any) => {
  if (percent < 0.01) return null;
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={500}
    >
      {`${name} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
};

export const SimplePieChart: React.FC<PieChartProps> = ({
  data,
  dataKey,
  nameKey = 'name',
  title,
  height = 300,
  onSliceClick,
}) => (
  <div style={{ width: '100%' }}>
    {title && (
      <Title level={5} style={{ marginBottom: 16, fontWeight: 600, color: '#1F2937' }}>
        {title}
      </Title>
    )}
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey={dataKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          outerRadius={Math.min(height * 0.35, 100)}
          innerRadius={Math.min(height * 0.18, 50)}
          paddingAngle={2}
          label={renderPieLabel}
          labelLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
          stroke="#fff"
          strokeWidth={2}
          style={onSliceClick ? { cursor: 'pointer' } : undefined}
          onClick={onSliceClick ? (entry: any) => onSliceClick(entry?.[nameKey] || entry?.name) : undefined}
        >
          {data.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<PieTooltip />} />
        <Legend
          verticalAlign="bottom"
          iconType="circle"
          iconSize={8}
          formatter={(value: string) => (
            <span style={{ color: '#4B5563', fontSize: 12 }}>{value}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  </div>
);

// ─── Area Chart ──────────────────────────────────────────────────

interface AreaChartProps {
  data: ChartData[];
  dataKey: string;
  xKey?: string;
  title?: string;
  height?: number;
  color?: string;
}

export const SimpleAreaChart: React.FC<AreaChartProps> = ({
  data,
  dataKey,
  xKey = 'name',
  title,
  height = 300,
  color = '#4F6BF6',
}) => (
  <div style={{ width: '100%' }}>
    {title && (
      <Title level={5} style={{ marginBottom: 16, fontWeight: 600, color: '#1F2937' }}>
        {title}
      </Title>
    )}
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`areaGrad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
        <XAxis
          dataKey={xKey}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 11, fill: '#6B7280' }}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 11, fill: '#9CA3AF' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2.5}
          fill={`url(#areaGrad-${color.replace('#', '')})`}
          dot={{ r: 3, fill: '#fff', stroke: color, strokeWidth: 2 }}
          activeDot={{ r: 5, fill: color, stroke: '#fff', strokeWidth: 2 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);
